import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/FindJoinedCampaigns.graphql';
import { Query, QueryfindJoinedCampaignsArgs } from '../../__generated__/globalTypes';

export const useFindJoinedCampaignsQuery = (options?: QueryHookOptions<Query, QueryfindJoinedCampaignsArgs>) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QueryfindJoinedCampaignsArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useFindJoinedCampaignsLazyQuery = (
  options?: LazyQueryHookOptions<Query, QueryfindJoinedCampaignsArgs>
): QueryResult<Query, QueryfindJoinedCampaignsArgs> & {
  getFindJoinedCampaigns: LazyQueryExecFunction<Query, QueryfindJoinedCampaignsArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getFindJoinedCampaigns, result] = useLazyQuery<Query, QueryfindJoinedCampaignsArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return { getFindJoinedCampaigns, ...result };
};

export const useFindJoinedCampaignsPromiseQuery = () => {
  const client = useApolloClient();

  const getFindJoinedCampaigns = useCallback(
    (options?: Omit<QueryOptions<QueryfindJoinedCampaignsArgs, Query>, 'query'>) =>
      client.query<Query, QueryfindJoinedCampaignsArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getFindJoinedCampaigns };
};
// Suspense Query Hooks
export const useFindJoinedCampaignsSuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QueryfindJoinedCampaignsArgs>
) => {
  const result = useSuspenseQuery<Query, QueryfindJoinedCampaignsArgs>(QUERY, {
    ...options
  });

  return result;
};
